import { useEffect, useRef, useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { connectEventStream, type AgentEvent } from '@/api/eventStream'
import { useAuthStore } from '@/stores/authStore'
import { useTimerStore } from '@/stores/timerStore'
import { useEventNotificationStore } from '@/stores/eventNotificationStore'
import { taskKeys } from './useTasks'
import { timelogKeys } from './useTimeLogs'
import { dashboardKeys } from './useDashboardStats'
import { teamKeys } from './useTeamWorkload'

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected'

export function useAgentEventStream() {
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useAuthStore()
  const { startTimer, stopTimer } = useTimerStore()
  const { addNotification, clearOld } = useEventNotificationStore()
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const disconnectRef = useRef<(() => void) | null>(null)

  const handleEvent = useCallback(
    (event: AgentEvent) => {
      const currentUserId = user?.id
      if (!currentUserId) return

      const isSelf = event.source_user_id === currentUserId

      switch (event.event_type) {
        case 'task_created': {
          queryClient.invalidateQueries({ queryKey: taskKeys.all })
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
          queryClient.invalidateQueries({ queryKey: teamKeys.all })
          if (isSelf || event.affected_user_ids.includes(currentUserId)) {
            addNotification({
              id: event.event_id,
              type: 'task_created',
              title: isSelf ? 'Task created' : 'New task assigned',
              description: (event.payload.title as string) || 'A new task was created',
              taskId: event.payload.task_id as string,
            })
          }
          break
        }

        case 'task_assigned': {
          queryClient.invalidateQueries({ queryKey: taskKeys.all })
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
          queryClient.invalidateQueries({ queryKey: teamKeys.all })
          if (isSelf || event.payload.assignee_id === currentUserId) {
            const assigneeName = event.payload.assignee_name as string | undefined
            addNotification({
              id: event.event_id,
              type: 'task_assigned',
              title: isSelf ? `Task assigned to ${assigneeName || 'team member'}` : 'Task assigned to you',
              description: (event.payload.title as string) || 'A task was assigned',
              taskId: event.payload.task_id as string,
            })
          }
          break
        }

        case 'task_updated': {
          queryClient.invalidateQueries({ queryKey: taskKeys.all })
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
          if (isSelf || event.affected_user_ids.includes(currentUserId)) {
            addNotification({
              id: event.event_id,
              type: 'task_updated',
              title: isSelf ? 'Task updated' : 'Task updated',
              description: `${event.payload.title}: ${event.payload.old_status} → ${event.payload.new_status}`,
              taskId: event.payload.task_id as string,
            })
          }
          break
        }

        case 'timer_started': {
          if (isSelf && event.payload.task_id && event.payload.task_title) {
            startTimer(
              event.payload.task_id as string,
              event.payload.task_title as string,
            )
          }
          break
        }

        case 'timer_stopped': {
          if (isSelf) {
            stopTimer()
          }
          queryClient.invalidateQueries({ queryKey: timelogKeys.all })
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
          break
        }

        case 'time_logged': {
          queryClient.invalidateQueries({ queryKey: timelogKeys.all })
          queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
          break
        }

        case 'notification_sent': {
          if (event.affected_user_ids.includes(currentUserId)) {
            addNotification({
              id: event.event_id,
              type: 'notification_sent',
              title: 'New notification',
              description: (event.payload.message_preview as string) || 'You have a new notification',
            })
          }
          break
        }
      }
    },
    [queryClient, user?.id, startTimer, stopTimer, addNotification],
  )

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus('disconnected')
      return
    }

    setStatus('connecting')

    const disconnect = connectEventStream({
      onEvent: handleEvent,
      onConnected: () => setStatus('connected'),
      onDisconnected: () => setStatus('disconnected'),
      onError: (err) => console.warn('Event stream error:', err.message),
    })

    disconnectRef.current = disconnect

    return () => {
      disconnect()
      disconnectRef.current = null
    }
  }, [isAuthenticated, handleEvent])

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: taskKeys.all })
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all })
        queryClient.invalidateQueries({ queryKey: teamKeys.all })
        queryClient.invalidateQueries({ queryKey: timelogKeys.all })
        clearOld()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [queryClient, clearOld])

  return { status }
}
